import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { NavigationGuard, RawLocation } from 'vue-router';
import { AuthStrategyType, RouteAccess } from '../../tenants/types';

/** Rutas (ya resueltas a path) que necesita una estrategia de acceso. */
export interface AuthStrategyOptions {
    /** Path del layout raíz (`/`). */
    rootPath: string;
    /** Path al que va un usuario con sesión que entra a `/` o al login (obligatorio para `AuthStrategyType.Login`). */
    landingPath?: string;
    /** Path del login (obligatorio para `AuthStrategyType.Login`). */
    loginPath?: string;
    /** Nombre del parámetro de ruta que trae el token (obligatorio para `AuthStrategyType.UrlToken`). */
    tokenParam?: string;
}

/**
 * Lo que la app ve del acceso (router, `HeaderToolbar`): lo implementa `AccessCoordinator`, que delega en la
 * estrategia de la ruta activa. Se provee con `InjectionKey.Access`.
 */
export interface AccessControl {
    /** Si el usuario puede cerrar sesión (muestra el botón "Cerrar Sesión" en la barra superior). */
    readonly supportsLogout: boolean;
    /** Token vigente o `null`. */
    getToken(): string | null;
    isAuthenticated(): boolean;
    /** Guard global del router (`router.beforeEach`). */
    guard: NavigationGuard;
    /** Cierra la sesión y devuelve a dónde navegar después (o `null` si no corresponde navegar). */
    logout(): RawLocation | null;
}

/**
 * Estrategia de acceso (patrón Strategy). Encapsula cómo se obtiene el token, cómo viaja en cada request, qué pasa
 * ante un 401 y qué rutas puede ver el usuario. No registra interceptores: `AccessCoordinator` los instala una sola
 * vez sobre `Http` y delega en la estrategia de la ruta activa.
 */
export interface AuthStrategy extends AccessControl {
    readonly type: AuthStrategyType;
    /** Pone el `Authorization` de esta estrategia en la request (por request, nunca en `defaults`). */
    applyAuthorization(config: AxiosRequestConfig): AxiosRequestConfig;
    /** Manejo de una respuesta 401: reintentar con `http` (p. ej. refresh) o rechazar / cerrar la sesión. */
    handleUnauthorized(error: AxiosError, http: AxiosInstance): Promise<any>;
}

/** Acceso de la ruta destino (`meta.access` del record hoja); una ruta sin `access` se trata como pública. */
export const routeAccessOf = (route: { meta?: any }): RouteAccess =>
    (route.meta && (route.meta.access as RouteAccess)) || RouteAccess.Public;
